import {
  appendToConversationData,
  showAutoDialog,
} from 'src/game/conversation/conversation.utils';
import { startDuoResponseSequence } from 'src/game/duo/duo.sequence';
import StateObserver, { user } from 'src/StateObserver';
import * as Fate from 'src/replicant/fate';
import { getTutorialController } from '../tutorial/TutorialController';
import { PetEvolutionStage, PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { levelUpSequence } from '../level/level.sequence';
import {
  getCreativeSuffix,
  getSceneManager,
  waitForAssets,
} from 'src/lib/utils';
import { petSpeciesAssets } from 'src/loadingGroups';
import { requestRateApp } from 'src/lib/platformUtils';
import { PetController } from './PetController';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import PetNames from 'src/replicant/data/PetNames.json';
import { getPet } from '.';
import { Feature } from 'src/replicant/analytics';
import { RevivalPopupInitArgs } from '../visit/popups/PopupRevivalTall';
import { RevivalConfirmPopupInitArgs } from '../visit/popups/PopupRevivalConfirm';

/**
 * Sequence triggered whenever pet comes back from an action.
 * E.g. when pet comes back from a care action, when pet comes back from a visit, etc...
 */
export async function petIdleSequence() {
  const { user } = StateObserver.getState();

  const species = Pet.getters.getEquippedPetSpecies(user);
  if (!species) {
    return;
  }

  // Make sure pet assets are loaded.
  await waitForAssets(petSpeciesAssets[species], {
    feature: Feature.PET._,
    subFeature: Feature.PET.ASSETS,
  });
  appendToConversationData(species);

  // Start stage upgrade sequence if needed

  // Pet should not be idle while in daycare
  if (
    Fate.getters.hasPermanentStatusEffect(user, 'daycare', StateObserver.now())
  ) {
    return;
  }

  if (user.duo.debut && (await startDuoResponseSequence())) {
    return;
  }

  // Only consider revived if the pet is not dead
  const revived =
    user.visit.revivedBy &&
    !Fate.getters.hasPermanentStatusEffect(user, 'dead', StateObserver.now());

  if (!revived) {
    await showAutoDialog();
  } else {
    // Only show the revival popup if the pet is not dead, otherwise it'd be confusing.
    await showRevivalPopup({
      mode: 'revived',
      species: Pet.getters.getCurrentEvolution(user)!.species,
      friendName: user.visit.revivedBy,
    });
    await getPet().startConversation('friend_petrevived');
  }

  if (user.visit.revivedBy) {
    // Mark the notification as read.
    await StateObserver.invoke.markReviveNotificationAsRead();
  }
}

export async function evolutionSequence(pet: PetController) {
  // Clear encyclopedia notifications, if the user has the collection feature
  await StateObserver.invoke.clearEncyclopediaNotifications();

  // Prevent pet conversations while evolving
  pet.conversation.pause();

  // Wait for conversation finish
  await pet.conversation.waitForFinish();

  // Move to the center of the living room and wait
  await pet.moveTo({ scene: getSceneManager().baseSceneId, location: 'c' });
  pet.position.setWandering(false);
  pet.appearance.setAction('evolve');

  // Store previous state;
  const { user } = StateObserver.getState();
  const evolution = Pet.getters.getCurrentEvolution(user)!;
  const originalSpecies = evolution.species;
  const originalStage = evolution.stage!;

  await StateObserver.invoke.evolvePet();

  // Simulate the evolution steps within this single loop,
  // without affecting other parts of the game
  while (await presentEvolution(pet, originalSpecies, originalStage)) {
    // Reset the pet to the previous form
    pet.appearance.setSpecies(originalSpecies);

    // Tell it it's evolving so it doesn't change to the new form with the action
    pet.appearance.setAction('evolve');

    // Roll a new pet
    await StateObserver.invoke.redoEvolution();

    // Since it's not truly evolving, force the evolving dialog
    await pet.conversation.begin('normal_chat', originalSpecies, 'evolving');

    // Unlock the action to allow emitters to function normally
    pet.appearance.setAction(undefined);
  }

  // User did not want to redo anymore, happy with current outcome
  if (StateObserver.getState().user.evolutionRewind) {
    await StateObserver.invoke.acceptEvolution(false);
  }

  // Set the pet free
  pet.position.setWandering(true);
  await getTutorialController().handleEvent('petEvolved');

  // Show level up sequence if needed.
  await levelUpSequence(undefined);

  // Request rate app if pet has evolved to adult.
  if (pet.stage === 'adult') {
    await getTutorialController().handleEvent('transformUnlocked');
    await requestRateApp();
  }
}

async function presentEvolution(
  pet: PetController,
  species: PetSpeciesID,
  stage: PetEvolutionStage,
): Promise<boolean> {
  const newSpecies = pet.species;
  if (!newSpecies) {
    throw new Error('Invalid evolution attempt');
  }

  await Promise.all([
    pet.setEvolutionEffectsVisible(true),
    waitForAssets(petSpeciesAssets[newSpecies], {
      feature: Feature.PET._,
      subFeature: Feature.PET.EVOLUTION_ASSETS,
    }),
  ]);
  appendToConversationData(newSpecies);

  pet.appearance.setSpecies(newSpecies);
  await pet.setEvolutionEffectsVisible(false);

  // Re-enable pet conversations and show initial greetings
  pet.conversation.start();
  await pet.startConversation('initial_greetings');

  return false;
}

export async function sleepSequence(pet: PetController) {
  await pet.conversation.waitForFinish();
  await pet.moveTo({
    scene: 'bedroom',
    location: 'bed',
  });
  pet.appearance.unlock();
  pet.appearance.setAction('sleep');
}

export async function renamePetSequence(args: {
  defaultName?: string;
  origin: PopupDialogOrigin | undefined;
}): Promise<string | undefined> {
  let { defaultName, origin } = args;

  if (!defaultName) {
    defaultName = getRandomItemFromArray(PetNames, Math.random)[
      `name_${getCreativeSuffix()}`
    ];
  }

  // Ask new name
  const newName = await openPopupPromise('popupPetNameInput', {
    name: defaultName,
    origin,
  });

  // Update actual name
  if (newName) {
    await StateObserver.invoke.updateName(newName);
    getPet().appearance.setAttributes({ name: newName });
  }

  return newName;
}

export async function showRevivalPopup(args: RevivalPopupInitArgs) {
  return openPopupPromise('popupRevivalSimple', args);
}

export async function showRevivalConfirmPopup(
  args: RevivalConfirmPopupInitArgs,
) {
  return openPopupPromise('popupRevivalConfirmSimple', args);
}
